import { PlayerProfile, UpgradeTarget } from "./profile";

/**
 * What catching a wild pet pays (Pets v2, docs/PETS.md; Kevin: "if you beat it then you unlock the pet, or if you
 * already have that pet, it levels up … Pets can go to the same level as towers"). Pure rules on the profile; the app
 * pays it once per run (app pets.ts) and adds the skin the pet wore to the cosmetics.
 *
 * - Not owned yet: owned at level 1.
 * - Owned, below its cap (the highest tower level, meta/profile.ts petLevelCap): +1 level. When its upgrade is running,
 *   the catch finishes that upgrade now instead (its end moves to `now`; the normal claim raises the level and frees the
 *   builder), so no gold is wasted; an upgrade queued on it is paid back when its level no longer follows (upgradeQueue).
 * - At its cap: the gold its next upgrade would cost (at the towers' max, the last level's), paid now.
 */
export type CatchReward =
  | { readonly kind: "unlocked"; readonly petId: string; readonly level: number }
  | { readonly kind: "levelUp"; readonly petId: string; readonly from: number; readonly to: number; readonly finishedJob: number | null }
  | { readonly kind: "gold"; readonly petId: string; readonly level: number; readonly gold: number };

export function applyCatch(profile: PlayerProfile, petId: string, now: number): CatchReward | null {
  const pets = profile.pets, index = pets.data.list.findIndex((p) => p.id === petId);
  if (index < 0) return null;
  if (pets.unlock(petId)) return { kind: "unlocked", petId, level: 1 };
  const level = pets.levelAt(index);
  const job = profile.jobs.find((j) => j.target === UpgradeTarget.Pet && j.towerIndex === index);
  if (job !== undefined) {
    job.endAt = Math.min(job.endAt, now);
    return { kind: "levelUp", petId, from: level, to: job.toLevel, finishedJob: job.id };
  }
  if (level < profile.petLevelCap) {
    pets.raiseLevelAt(index, level + 1);
    return { kind: "levelUp", petId, from: level, to: level + 1, finishedJob: null };
  }
  const gold = profile.costFor(Math.min(level + 1, profile.balance.tables.maxLevel));
  profile.addGold(gold);
  return { kind: "gold", petId, level, gold };
}
